import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Switch, Space, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { useMenuPermission } from '../hooks/useMenuPermission';

function ApiConfig() {
  const { canEdit } = useMenuPermission();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api-configs');
      if (res.code === 200) setData(res.data);
    } catch (err) {}
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ auto_query: false, auto_query_final: false, enabled: true, delay_ms: 500 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      let res;
      if (editing) {
        res = await api.put(`/api-configs/${editing.id}`, values);
      } else {
        res = await api.post('/api-configs', values);
      }
      if (res.code === 200) {
        message.success(editing ? '更新成功' : '创建成功');
        setModalVisible(false);
        loadData();
      }
    } catch (err) {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/api-configs/${id}`);
      if (res.code === 200) { message.success('删除成功'); loadData(); }
    } catch (err) {}
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '接口名称', dataIndex: 'name', width: 150 },
    { title: '平台', dataIndex: 'platform', width: 100, render: v => <Tag color="blue">{v}</Tag> },
    { title: 'Key', dataIndex: 'api_key', width: 200, ellipsis: true, render: v => v ? v.substring(0, 20) + '...' : '-' },
    {
      title: '初始粉丝', dataIndex: 'auto_query', width: 80,
      render: v => v ? <Tag color="green">是</Tag> : <Tag>否</Tag>
    },
    {
      title: '最终粉丝', dataIndex: 'auto_query_final', width: 80,
      render: v => v ? <Tag color="green">是</Tag> : <Tag>否</Tag>
    },
    {
      title: '启用', dataIndex: 'enabled', width: 80,
      render: v => v ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag>
    },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, record) => (
        <Space>
          {canEdit && <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>}
          {canEdit && (
            <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>接口配置</h3>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增接口</Button>}
      </div>
      <Table rowKey="id" columns={columns} dataSource={data} loading={loading} pagination={false} />
      <Modal title={editing ? '编辑接口' : '新增接口'} open={modalVisible} onOk={handleSave} onCancel={() => setModalVisible(false)} confirmLoading={saving}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="接口名称" rules={[{ required: true }]}>
            <Input placeholder="如：抹茶DyApi" />
          </Form.Item>
          <Form.Item name="platform" label="平台" rules={[{ required: true }]}>
            <Select options={[
              { label: '抖音号', value: '抖音' },
              { label: '视频号', value: '视频号' },
              { label: '小红书', value: '小红书' }
            ]} />
          </Form.Item>
          <Form.Item name="api_key" label="Key" rules={[{ required: true, message: '请输入接口Key' }]}>
            <Input placeholder="Authorization Bearer 的值" />
          </Form.Item>
          <Form.Item name="auto_query" label="录入时自动查询初始粉丝" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="auto_query_final" label="完成时自动查询最终粉丝" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="delay_ms" label="查询延时(毫秒)">
            <Select options={[{label:'无延时',value:0},{label:'500ms',value:500},{label:'1000ms',value:1000},{label:'2000ms',value:2000},{label:'3000ms',value:3000},{label:'5000ms',value:5000}]} />
          </Form.Item>
          <Form.Item name="enabled" label="是否启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ApiConfig;
